import { Money, type Currency, type ReceiptChannel, type ReceiptInput } from '@chukua/common';

export interface ReceiptFormValues {
  channel: ReceiptChannel;
  reference?: string;
  customerId: string;
  amount: string;
  currency: Currency;
  /** `YYYY-MM-DD`, as the API takes it. */
  receivedOn: string;
  companyAccountId: string;
  payer: string;
}

export type AmountCheck = { ok: true; amountMinor: number } | { ok: false; reason: string };

/** The amount as typed, in major units ("12,500.50"), to the integer minor units the API stores. A float never reaches the wire. */
export function parseAmount(raw: string | undefined, currency: Currency): AmountCheck {
  const typed = (raw ?? '').replace(/[,\s]/g, '');
  if (typed === '') return { ok: false, reason: 'Enter the amount received' };
  try {
    const { amountMinor } = Money.parse(typed, currency).toJSON();
    return amountMinor > 0 ? { ok: true, amountMinor } : { ok: false, reason: 'The amount must be more than zero' };
  } catch {
    return { ok: false, reason: `Enter a number with at most the decimals ${currency} uses` };
  }
}

/** Assumes `parseAmount` passed, which the form's validator guarantees before submit. */
export function toReceiptInput(values: ReceiptFormValues): ReceiptInput {
  const amount = parseAmount(values.amount, values.currency);
  if (!amount.ok) throw new RangeError(amount.reason);
  return {
    customerId: values.customerId,
    channel: values.channel,
    reference: values.reference?.trim() || undefined,
    amountMinor: amount.amountMinor,
    currency: values.currency,
    receivedOn: values.receivedOn,
    payer: values.payer.trim(),
    companyAccountId: values.companyAccountId,
  };
}

/** The toast after the server confirms: a bank transfer may have no reference to name. */
export function recordedMessage(reference: string | null | undefined): string {
  return reference ? `Receipt ${reference} recorded` : 'Receipt recorded';
}
